import { useEffect, useState } from "react";
import { Megaphone, TrendingUp } from "lucide-react";
import api from "../../lib/api";

const fmtEur = (cents, currency = "EUR") => {
    const v = (cents || 0) / 100;
    try {
        return new Intl.NumberFormat("de-DE", { style: "currency", currency, minimumFractionDigits: v % 1 === 0 ? 0 : 2 }).format(v);
    } catch { return `${v.toFixed(2)} €`; }
};

function Kpi({ label, value, testid }) {
    return (
        <div className="bg-white border border-gray-200 rounded-sm p-3">
            <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">{label}</div>
            <div className="font-mono-num text-xl font-black text-[#111827]" data-testid={testid}>{value}</div>
        </div>
    );
}

function AttrCard({ label, total, testid }) {
    const sum = total ? Object.values(total).reduce((a, b) => a + (b || 0), 0) : 0;
    return (
        <div className="bg-white border border-gray-200 rounded-sm p-3">
            <div className="text-[9px] uppercase tracking-wider font-bold text-gray-500">{label}</div>
            <div className="font-mono-num text-lg font-black text-[#111827]" data-testid={testid}>{sum}</div>
        </div>
    );
}

/**
 * MarketingStatsPanel — Empfehlungs-/Transaktions-Statistik (Umsatz, Punkte,
 * Transaktionen + Werbemittel-Tracking). Aus dem Marketing-Banner in den
 * „Aktivität"-Bereich des Vermittler-Dashboards verschoben (User-Wunsch).
 */
export default function MarketingStatsPanel() {
    const [stats, setStats] = useState(null);

    useEffect(() => {
        let alive = true;
        api.get("/users/me/marketing-stats")
            .then((r) => { if (alive) setStats(r.data); })
            .catch(() => { if (alive) setStats(null); });
        return () => { alive = false; };
    }, []);

    return (
        <div className="bg-gray-50 border border-gray-200 rounded-sm p-4 mb-4" data-testid="marketing-stats-panel">
            <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-[#FFCC00]" />
                <h3 className="font-display font-bold text-sm">Empfehlungs-Statistik</h3>
            </div>
            <div className="grid grid-cols-3 gap-3" data-testid="marketing-kpis">
                <Kpi label="Erfasster Umsatz" testid="marketing-revenue" value={stats == null ? "—" : fmtEur(stats.total_revenue_cents, stats.currency)} />
                <Kpi label="Erhaltene Punkte" testid="marketing-points" value={stats == null ? "—" : (stats.points_earned ?? 0)} />
                <Kpi label="Transaktionen" testid="marketing-transactions" value={stats == null ? "—" : (stats.transaction_count ?? 0)} />
            </div>
            <div className="mt-2 text-[10px] text-gray-500">
                Punkte-Regel: 1 Punkt pro 1 € Umsatz. Punkte fließen in das Tarif-/Vermittler-Punktesystem ein.
            </div>

            {stats && stats.attribution && (
                <div className="mt-4 pt-4 border-t border-gray-200" data-testid="marketing-attribution-section">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-2 flex items-center gap-2">
                        <Megaphone className="w-3 h-3" /> Werbemittel-Tracking
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <AttrCard label="Heute" testid="attr-today" total={stats.attribution.today} />
                        <AttrCard label="7 Tage" testid="attr-7d" total={stats.attribution.last_7d} />
                        <AttrCard label="30 Tage" testid="attr-30d" total={stats.attribution.last_30d} />
                        <div className="bg-white border border-gray-200 rounded-sm p-3">
                            <div className="text-[9px] uppercase tracking-wider font-bold text-gray-500">Unique Besucher 30d</div>
                            <div className="font-mono-num text-lg font-black text-[#111827]" data-testid="attr-unique-visitors-30d">
                                {stats.attribution.unique_visitors_30d ?? 0}
                            </div>
                        </div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px]" data-testid="attr-30d-breakdown">
                        {["banner", "landing", "qr", "pageview"].map((k) => (
                            <div key={k} className="flex items-center justify-between bg-white border border-gray-200 rounded-sm px-2 py-1.5" data-testid={`attr-30d-kind-${k}`}>
                                <span className="uppercase tracking-wider text-gray-500">{k}</span>
                                <span className="font-mono-num font-bold text-[#111827]">{(stats.attribution.last_30d || {})[k] ?? 0}</span>
                            </div>
                        ))}
                    </div>
                    <div className="mt-3 flex items-center justify-between text-[10px] text-gray-500">
                        <span>Conversion-Rate 30d (Klicks → bezahlte Transaktionen):</span>
                        <span className="font-mono-num font-bold text-[#111827]" data-testid="attr-conversion-rate-30d">
                            {((stats.conversion_rate_30d ?? 0) * 100).toFixed(2)}%
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}
